/* eslint-disable import/no-extraneous-dependencies -- for adobe spectrum, aria, stately. */
import { Notification, Provider as EleveoProvider } from '@eleveo/components-react';
import React, { useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import { AuthorizationConsumer, AuthorizationProvider } from './app/AuthorizationProvider';
import { ConfigurationProvider } from './app/ConfigurationProvider';
import { DataProvider } from './app/DataProvider';
import { SessionProvider } from './app/SessionProvider';
import { RuntimeConfiguration } from './constants/integrationConstants';
import { Router } from './router/Router';
import { useReactQueryCache } from './utils/reactQueryUtils';

export const App: React.FC<RuntimeConfiguration> = ({ env }) => {
  const queryCache = useReactQueryCache();

  const reactQueryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          /**
           * Defaults to 0ms, that means react query will fetch data every time query is rendered.
           * Value of `staleTime` needs to be tested and changed over time according to needs of this application.
           */
          staleTime: 20 * 1000, // 20s
        },
      },
      queryCache,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigurationProvider env={env}>
      <AuthorizationProvider>
        <SessionProvider>
          <HelmetProvider>
            <DataProvider>
              <AuthorizationConsumer>
                {({ locale }) => (
                  <EleveoProvider locale={locale}>
                    <Notification newestOnTop />
                    <QueryClientProvider client={reactQueryClient}>
                      <ReactQueryDevtools initialIsOpen={false} position="top-left" />
                      <Router />
                    </QueryClientProvider>
                  </EleveoProvider>
                )}
              </AuthorizationConsumer>
            </DataProvider>
          </HelmetProvider>
        </SessionProvider>
      </AuthorizationProvider>
    </ConfigurationProvider>
  );
};
